import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { BookingLanguage, BookingStep, TimeSlot } from "@/types/booking";
import { Product, formatPrice } from "./product-step/types";
import { AddOnSection } from "./add-ons-step/AddOnSection";
import { ArrowRight } from "lucide-react";
import { PriceDisplay } from "./product-step/PriceDisplay";
import { ExpandableOrderSummary } from "./booking/ExpandableOrderSummary";
import { useClinics } from "@/hooks/useClinics";
import { t as translate, resolveProductKey } from "@/locales";
import { useMembership } from "@/contexts/MembershipContext";
import { isBloodTestVisible } from "@/utils/bloodTestVisibility";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import { useMembershipTiers } from "@/hooks/useMembershipTiers";
import { getCadencePriceChf } from "@/utils/membershipPricing";
import { useMarket } from "@/hooks/useMarket";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import {
  computeBundlePricing,
  isBloodMembershipBaseProduct,
} from "@/utils/bloodMembershipPricing";
import { MriScanSection } from "./add-ons-step/MriScanSection";
import { useBookingFlow } from "@/hooks/useBookingFlow";
import { marketHasMembership } from "@/config/market";

interface AddOnsStepProps {
  onContinue: () => void;
  onBack: () => void;
  language: BookingLanguage;
  packageProduct: Product;
  allProducts: Product[];
  selectedAddOns: string[];
  onAddOnToggle: (productId: string) => void;
  setCurrentStep?: (step: BookingStep) => void;
  selectedPackageType?: string; // Filter add-ons by package type
  // Additional props for complete order summary
  selectedClinic?: string;
  selectedTimeSlots?: TimeSlot[];
  globalSelectedProducts?: string[];
  /** When provided, renders this in place of the add-ons grid (and hides the
   *  "Select your add-ons" header). Used to reuse the step chrome — real
   *  sidebar + order summary — for the membership cadence page. */
  contentOverride?: React.ReactNode;
}

const AddOnsStep: React.FC<AddOnsStepProps> = ({
  onContinue,
  onBack,
  language,
  packageProduct,
  allProducts,
  selectedAddOns,
  onAddOnToggle,
  setCurrentStep,
  selectedPackageType,
  selectedClinic,
  selectedTimeSlots = [],
  globalSelectedProducts = [],
  contentOverride,
}) => {
  const { data: clinics } = useClinics();
  const {
    isMember,
    currentEntitlementType,
    showAllBloodTestsFlag,
    getEffectivePrice,
  } = useMembership();

  // With a membership in the cart the standalone advanced blood test doesn't
  // apply — the membership IS that test (Sven, 2026-08-04). The checkout edge
  // function has always dropped it from one-off items, so offering it here
  // quoted a line the charge would never contain.
  const membershipInCart = useMembershipPlanStore((s) => s.active);

  // Filter and sort add-on products (those that are not packages and not already in the package)
  const addOns = allProducts
    .filter((product) => {
      if (product.product_type === "package") return false;
      if (membershipInCart && isBloodMembershipBaseProduct(product))
        return false;
      if (packageProduct.components?.includes(product.id)) return false;
      if (packageProduct.excluded_addon_ids?.includes(product.id)) return false;
      const statusOk =
        product.status === "active" ||
        (product.status === "membership" &&
          (isMember || showAllBloodTestsFlag));
      if (!statusOk) return false;
      if (
        selectedPackageType &&
        !(product.package_types || []).includes(selectedPackageType)
      ) {
        return false;
      }
      return isBloodTestVisible(product.stripe_product_id, {
        isMember,
        currentEntitlementType,
        showAllBloodTestsFlag,
      });
    })
    .sort((a, b) => {
      // Sort by sort_order if available
      if (a.sort_order != null && b.sort_order != null) {
        return a.sort_order - b.sort_order;
      }

      // If only one has sort_order, prioritize it
      if (a.sort_order != null && b.sort_order == null) return -1;
      if (a.sort_order == null && b.sort_order != null) return 1;

      // Fallback to alphabetical sorting
      return a.name.localeCompare(b.name);
    });

  // Calculate total price including package and selected add-ons.
  // Uses getEffectivePrice so membership discounts and entitled (free) items
  // are reflected in the collapsed summary bar.
  const totalPrice = useMemo(() => {
    const packagePrice = getEffectivePrice(packageProduct);
    const addOnsPrice = selectedAddOns.reduce((sum, addOnId) => {
      const addOn = allProducts.find((p) => p.id === addOnId);
      return sum + (addOn ? getEffectivePrice(addOn) : 0);
    }, 0);
    return packagePrice + addOnsPrice;
  }, [packageProduct, selectedAddOns, allProducts, getEffectivePrice]);

  // Membership cadence: the order summary (incl. the mobile ExpandableOrderSummary)
  // shows a single "Ahead membership" line priced at the selected cadence (real
  // tier price, Stripe source of truth) instead of the baseline product + add-ons.
  // Swiss-only + feature-flagged: the membership summary only surfaces when the
  // market is CH and the membership_v2 flag is on.
  const { market, currency } = useMarket();
  const { data: featureFlags } = useFeatureFlags();
  const membershipFlowEnabled =
    marketHasMembership(market) && (featureFlags?.["membership_v2"] ?? true);
  const planActiveRaw = useMembershipPlanStore((s) => s.active);
  const membershipPlanActive = membershipFlowEnabled && planActiveRaw;
  const membershipPlan = useMembershipPlanStore((s) => s.plan);
  const membershipTiers = useMembershipTiers().tiers;
  const membershipPriceChf = getCadencePriceChf(
    membershipPlan,
    membershipTiers,
  );
  const membershipProduct: Product = {
    id: "membership-plan",
    // Resolved via resolveProductKey in the order-summary renderer.
    name: "membershipCadence.orderLineName",
    description: null,
    images: [],
    default_price: {
      id: `membership-${membershipPlan}`,
      unit_amount: membershipPriceChf * 100,
      currency: "chf",
    },
    product_type: "package",
  };

  const setMembershipActive = useMembershipPlanStore((s) => s.setActive);
  const setBundleAttached = useMembershipPlanStore((s) => s.setBundleAttached);
  const setMembershipPlan = useMembershipPlanStore((s) => s.setPlan);
  const membershipFirst = useMembershipPlanStore((s) => s.membershipFirst);
  const isScanCart = selectedPackageType === "mri";
  // No membership upsell on this step. The aubergine "Add ongoing blood
  // tracking" card that used to sit at the top was removed on review (AHD-8140);
  // MRI-first users reach the membership through the "Join the membership &
  // save" block on the DETAILS step instead.

  const isBundle = membershipPlanActive && isScanCart;
  const bundleAttached = useMembershipPlanStore((s) => s.bundleAttached);
  const { attachScanToMembership, detachScanFromMembership } = useBookingFlow();

  // Membership-first flow: full-body scans offered as a quiet section on this
  // page (replaces the dedicated mri-crosssell step). MRI-first shows nothing
  // here: the user already has a scan in the cart.
  const showMriScanSection =
    membershipFlowEnabled && membershipPlanActive && membershipFirst;
  const mriScanProducts = useMemo(() => {
    if (!showMriScanSection) return [];
    return allProducts
      .filter(
        (p) =>
          p.product_type === "package" &&
          (p.package_types ?? []).includes("mri") &&
          !(p.package_types ?? []).includes("blood-only") &&
          p.status === "active" &&
          // Price-on-request packages can't be sold as a one-click attach.
          p.default_price != null,
      )
      .sort(
        (a, b) => a.default_price.unit_amount - b.default_price.unit_amount,
      );
  }, [showMriScanSection, allProducts]);
  const attachedScanId =
    isScanCart && bundleAttached ? packageProduct.id : null;
  // With a scan attached its imaging add-ons render nested inside the scan
  // section, so the standalone imaging section would duplicate them.
  const nestScanAddOns = attachedScanId !== null && showMriScanSection;
  const selectedAddOnProducts = selectedAddOns
    .map((id) => allProducts.find((p) => p.id === id))
    .filter((p): p is Product => !!p);
  const bundlePricing = isBundle
    ? computeBundlePricing(
        packageProduct,
        selectedAddOnProducts,
        membershipPriceChf * 100,
      )
    : null;
  // Bundle (scan + attached membership): pay-now = netted scan + member add-ons +
  // the cadence's first invoice. Standalone membership (blood-only) keeps the
  // either/or behaviour (the cadence line replaces the cart). Plain MRI uses totalPrice.
  const effectiveTotalPrice = bundlePricing
    ? bundlePricing.payNowCents
    : membershipPlanActive
      ? membershipPriceChf * 100
      : totalPrice;

  // Two recommended sections. `addOns` is already sorted by sort_order above,
  // so each section keeps that order and shows every item.
  const bloodAddOns = addOns.filter((a) => a.includes_blood_draw);
  const imagingAddOns = addOns.filter((a) => !a.includes_blood_draw);

  // DE market + blood-only cart: the imaging section header ("Bildgebung")
  // implies an MRT/MRI scan, which is irrelevant for a blood-only booking.
  // Swap it for a neutral "more add-on packages available" label instead.
  const isBloodOnly = selectedPackageType === "blood-only";
  const imagingHeaderKey =
    market === "de" && isBloodOnly
      ? "addOnsStep.imagingBloodOnlyDe"
      : "addOnsStep.recommendedImaging";

  const t = {
    back: translate(language, "common.back"),
    continue: translate(language, "common.continue"),
    totalPrice: translate(language, "productStep.totalPrice"),
    title: translate(language, "addOnsStep.title"),
    description: translate(language, "addOnsStep.subtitle"),
    bloodTests: translate(language, "addOnsStep.recommendedBloodTests"),
    imaging: translate(language, imagingHeaderKey),
  };

  // Get selected add-on details for the order summary
  const selectedAddOnDetails = useMemo(() => {
    return selectedAddOns
      .map((addOnId) => allProducts.find((p) => p.id === addOnId))
      .filter(Boolean)
      .sort((a, b) => {
        // Sort by sort_order if available
        if (a.sort_order != null && b.sort_order != null) {
          return a.sort_order - b.sort_order;
        }

        // If only one has sort_order, prioritize it
        if (a.sort_order != null && b.sort_order == null) return -1;
        if (a.sort_order == null && b.sort_order != null) return 1;

        // Fallback to alphabetical sorting
        return a.name.localeCompare(b.name);
      }) as Product[];
  }, [selectedAddOns, allProducts]);

  // Get global selected products (for complete order summary)
  const globalSelectedProductDetails = useMemo(() => {
    return globalSelectedProducts
      .map((productId) => allProducts.find((p) => p.id === productId))
      .filter(Boolean) as Product[];
  }, [globalSelectedProducts, allProducts]);

  // Get selected clinic object
  const selectedClinicObject = selectedClinic
    ? clinics?.find((c) => c.id === selectedClinic)
    : null;

  // Get centralized translations for order summary
  const orderSummaryTranslations = {
    orderSummary: translate(language, "orderSummary.title"),
    scan: translate(language, "orderSummary.scan"),
    addOns: translate(language, "orderSummary.addOns"),
    bloodTests: translate(language, "orderSummary.bloodTests"),
    location: translate(language, "orderSummary.location"),
    timeSlots: translate(language, "orderSummary.timeSlots"),
    totalPrice: translate(language, "orderSummary.totalPrice"),
    continue: translate(language, "common.next"),
    back: translate(language, "common.prev"),
    remove: translate(language, "common.remove"),
  };

  return (
    <>
      <div className="w-full">
        {/* Header Section — hidden when a content override (e.g. membership
            cadence) provides its own heading. */}
        {!contentOverride && (
          <div className="text-center mb-10 lg:mb-8 px-4 hidden lg:block">
            <h1
              className="mb-2"
              style={{
                fontWeight: 400,
                color: "#2D2D2D",
                fontSize: 24,
              }}
            >
              {t.title}
              {/* Member-prices chip rides inline in the heading, not on its
                  own line. */}
              {membershipPlanActive && (
                <span className="ml-2 inline-flex items-center rounded-full bg-deep-green-100 text-deep-green-700 text-sm font-medium px-3 py-1 align-middle relative -top-px whitespace-nowrap">
                  {translate(language, "addOnsStep.memberPrices")}
                </span>
              )}
            </h1>
            <p
              className="mx-auto leading-relaxed whitespace-pre-line"
              style={{ fontSize: 14, fontWeight: 400, color: "#2D2D2D" }}
            >
              {t.description}
            </p>
          </div>
        )}

        {contentOverride ? (
          <div className="pb-12">{contentOverride}</div>
        ) : (
          <div className="pb-12 flex flex-col gap-4">
            {/* Whatever the cart is built around comes first. A scan cart
                opens on imaging add-ons; a blood cart opens on blood. The
                second section carries the extra air — at the container's
                gap-4 alone the two headings read as one continuous list. */}
            {[
              {
                key: "blood",
                render: (
                  <AddOnSection
                    title={t.bloodTests}
                    products={bloodAddOns}
                    selectedAddOns={selectedAddOns}
                    onAddOnToggle={onAddOnToggle}
                    language={language}
                  />
                ),
              },
              {
                key: "imaging",
                render: nestScanAddOns ? null : (
                  <AddOnSection
                    title={t.imaging}
                    products={imagingAddOns}
                    selectedAddOns={selectedAddOns}
                    onAddOnToggle={onAddOnToggle}
                    language={language}
                  />
                ),
              },
            ]
              .sort((a, b) =>
                isScanCart
                  ? a.key === "imaging"
                    ? -1
                    : 1
                  : a.key === "blood"
                    ? -1
                    : 1,
              )
              .filter((s) => s.render !== null)
              .map((section, index) => (
                <div key={section.key} className={index > 0 ? "mt-6" : ""}>
                  {section.render}
                </div>
              ))}
            {showMriScanSection && (
              <MriScanSection
                scanProducts={mriScanProducts}
                attachedScanId={attachedScanId}
                onAttachScan={attachScanToMembership}
                onDetachScan={detachScanFromMembership}
                scanAddOns={nestScanAddOns ? imagingAddOns : []}
                selectedAddOns={selectedAddOns}
                onAddOnToggle={onAddOnToggle}
                language={language}
              />
            )}
          </div>
        )}
      </div>

      {/* Expandable Order Summary Footer */}
      <ExpandableOrderSummary
        totalPrice={effectiveTotalPrice}
        selectedProducts={
          isBundle
            ? // Bundle mode: pass the real scan so ExpandableOrderSummary's
              // isScanPackage check detects bundle correctly (the synthetic
              // membershipProduct has no package_types, breaking detection).
              [packageProduct]
            : membershipPlanActive
              ? [membershipProduct]
              : globalSelectedProductDetails.length > 0
                ? globalSelectedProductDetails
                : [packageProduct]
        }
        selectedAddOns={selectedAddOnDetails}
        selectedClinic={selectedClinicObject}
        selectedTimeSlots={selectedTimeSlots}
        language={language}
        onContinue={onContinue}
        onBack={onBack}
        showBackButton={true}
        showContinueButton={true}
        continueDisabled={false}
        onAddOnRemove={(addOnId) => onAddOnToggle(addOnId)}
        onEditProducts={
          setCurrentStep ? () => setCurrentStep("product") : undefined
        }
        onEditAddOns={
          setCurrentStep ? () => setCurrentStep("addons") : undefined
        }
        onEditLocation={
          setCurrentStep ? () => setCurrentStep("clinic") : undefined
        }
        onEditTimeSlots={
          setCurrentStep ? () => setCurrentStep("date") : undefined
        }
        translations={orderSummaryTranslations}
        currentStep="addons"
      />
    </>
  );
};

export default AddOnsStep;
